import { atom } from 'jotai';

import { type ComputerUseReadiness, isComputerUseUnsupported } from '@heidi/plugin-rpa';

import {
  emptyPermissionStatusMap,
  readPermissionStatuses,
} from './lib/check-permissions';
import { deriveMissingPermissions } from './lib/derive-missing-permissions';
import { requiredPermissions } from './matrix';
import type { PermissionMode, PermissionName, PermissionStatusMap } from './types';

export interface GateModalState {
  readonly open: boolean;
  readonly mode: PermissionMode;
  readonly missing: PermissionName[];
  readonly statuses: PermissionStatusMap;
}

export const closedGateModalState = (): GateModalState => ({
  open: false,
  mode: 'recording',
  missing: [],
  statuses: emptyPermissionStatusMap(),
});

/**
 * Modal/overlay state for the desktop permission gate. Rendered by
 * `<DesktopPermissionGateHost />`; consumers never read this directly.
 */
export const gateModalAtom = atom<GateModalState>(closedGateModalState());

/**
 * Imperative gate. Resolves `true` when the mode's permissions are already
 * granted; otherwise opens the gate modal and resolves `false` so the caller
 * can abort. Write-only action atom — consume via `useDesktopPermissionGate()`.
 */
export const ensurePermissionsAtom = atom(
  null,
  async (_get, set, mode: PermissionMode): Promise<boolean> => {
    const required = requiredPermissions(mode);
    const statuses = await readPermissionStatuses(required);
    const missing = deriveMissingPermissions(required, statuses);
    if (missing.length === 0) return true;
    set(gateModalAtom, { open: true, mode, missing, statuses });
    return false;
  }
);

type ReadinessState = ComputerUseReadiness['screen_capture'];

// A relaunch-pending grant reads Granted; the copy already says to restart Heidi, until the dialog gets its own restart row
const statusFromReadiness = (
  state: ReadinessState
): PermissionStatusMap[PermissionName] =>
  state === 'granted' || state === 'granted_needs_relaunch' ? 'granted' : 'denied';

export const showComputerUseRefusalAtom = atom(
  null,
  (_get, set, readiness: ComputerUseReadiness): void => {
    if (isComputerUseUnsupported(readiness)) return;
    const readinessByPermission: Partial<Record<PermissionName, ReadinessState>> = {
      screen_recording: readiness.screen_capture,
      accessibility: readiness.accessibility,
    };
    const statuses: PermissionStatusMap = {
      ...emptyPermissionStatusMap(),
      screen_recording: statusFromReadiness(readiness.screen_capture),
      accessibility: statusFromReadiness(readiness.accessibility),
    };
    // Rows follow readiness, so a relaunch-pending grant still explains the refusal
    const missing = requiredPermissions('computer_use').filter(
      (permission) => readinessByPermission[permission] !== 'granted'
    );
    set(gateModalAtom, { open: true, mode: 'computer_use', missing, statuses });
  }
);
